import React, { useEffect, useRef, useState } from "react";
import styles from "./assessment.module.scss";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "lib/i18n/use-translation";
import useAssessment from "../../hooks/useAssessment";
import {
  AssessmentModal,
  SaveProgressModal,
} from "apps/user/learner/components/modals";
import { usePatternBackground } from "providers/pattern-background";
import { useAuth } from "providers/auth";
import { logAnalyticsEvent } from "utility/FirebaseAnalytics";
import { logError } from "utility/Function";
import { savedProgressExpired } from "utility/savedProgress";
import AssessmentWelcomeUplift from "./AssessmentWelcomeUplift";
import AssessmentSectionUplift from "./AssessmentSectionUplift";
import AssessmentQuestionUplift from "./AssessmentQuestionUplift";
import AssessmentQuestionNumberUplift from "./AssessmentQuestionNumberUplift";
import AssessmentQuestionDurationUplift from "./AssessmentQuestionDurationUplift";
import AssessmentQuestionCountUplift from "./AssessmentQuestionCountUplift";
import AssessmentEndUplift from "./AssessmentEndUplift";
import { bucketFor, parseDurationResponse, formatDuration } from "./buckets";
import {
  buildOrderedDomains,
  getQuestionProgressNumber,
  getSectionProgressNumber,
} from "./progressDomains";
import { ProgressPillProps } from "./ProgressPill";
import {
  isCommercialSectionStep,
  isCommercialQuestionStep,
} from "./companySectionStep";
import CompanyQuestionsModal from "./CompanyQuestionsModal";
import {
  AssessmentQuestionStep,
  AssessmentStep,
  SelectInputOption,
  SelectInput,
  TextInput,
} from "apps/user/learner/api/types";
import {
  PageLoading,
  PageError,
} from "apps/user/learner/components/page-container";

import { AssessmentEvent, CompanyQuestionResponse } from "providers/Assessment";
import BluePattern from "Assets/patterns/blue.json";

// Matches the cream background every uplift step component paints directly
// (AssessmentEndUplift, AssessmentQuestionUplift, AssessmentSectionUplift,
// CompanyQuestionsModal) — keeps the canvas behind them the same color
// instead of the animated blue pattern.
const ASSESSMENT_BG_COLOR = "#f4efea";

// A step's stable, locale-independent handle for analytics and matching.
// `shorthand` is authored in the source CSV but nullable, and these values are
// substringed downstream, so fall back to the id rather than risk undefined —
// never to the title, which is the translated copy this keys away from.
const stepKey = (step: { shorthand?: string | null; id: string }): string =>
  step.shorthand ?? step.id;

// A chosen option's stable handle for analytics. `displayOrder` is the
// authored position, identical across environments; the id is a per-import
// primary key, so it is only the fallback for an option that somehow has no
// position. Never the label, which is translated.
const answerKey = (
  step: { shorthand?: string | null; id: string },
  option: { displayOrder?: number | null; id?: string | null }
): string => `${stepKey(step)}:${option.displayOrder ?? option.id ?? "unknown"}`;

const isAssessmentQuestionStep = (
  step: any
): step is AssessmentQuestionStep => {
  return step?.__typename === "AssessmentQuestionStep";
};

const isTextInput = (input: any): input is TextInput => {
  return input?.__typename === "TextInput";
};

const isSelectInput = (input: any): input is SelectInput => {
  return input?.__typename === "SelectInput";
};

// Submits on mount rather than waiting for a tap — used when the user isn't
// due to retake the optional company questions, so the end step has nothing
// to offer besides the submit action and its primer copy talks up questions
// that never arrive. Mirrors mobile's AutoCompleteEndStep.
const AutoCompleteEndStep: React.FC<{ onComplete: () => void }> = ({
  onComplete,
}) => {
  useEffect(() => {
    onComplete();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={styles["assessment-loading"]}
      data-testid="assessment-loading"
    >
      <PageLoading />
    </div>
  );
};

// Questions answered via an Hours/Minutes stepper instead of tap-to-select
// cards. Sleep duration defaults its stepper to 7h; activity questions
// default to 0h — matching the mobile uplift experience for the same
// shorthands.
const DURATION_SHORTHANDS = new Set<string>([
  "question_vigorous_activity_minutes",
  "question_moderate_activity_minutes",
  "question_sleep_duration",
]);
const SLEEP_DURATION_SHORTHAND = "question_sleep_duration";

const isDurationQuestionStep = (step: any): step is AssessmentQuestionStep =>
  isAssessmentQuestionStep(step) &&
  isSelectInput(step.input) &&
  DURATION_SHORTHANDS.has(step.shorthand ?? "");

// Questions answered via a single -/+ stepper instead of tap-to-select
// cards. Alcohol intake also authors its subtitleHtml as a "1 drink equals
// ..." equivalents list, rendered as a table instead of a plain hint line.
const COUNT_SHORTHANDS = new Set<string>([
  "question_alcohol_intake",
  "question_caffeine_intake",
]);
const EQUIVALENTS_SHORTHANDS = new Set<string>(["question_alcohol_intake"]);
// Count questions whose unit is per-day rather than per-week ("a day" vs "drinks").
const PER_DAY_COUNT_SHORTHANDS = new Set<string>(["question_caffeine_intake"]);

const isCountQuestionStep = (step: any): step is AssessmentQuestionStep =>
  isAssessmentQuestionStep(step) &&
  isSelectInput(step.input) &&
  COUNT_SHORTHANDS.has(step.shorthand ?? "");

const Assessment = () => {
  const {
    assessmentVersion,
    steps,
    step,
    currentstepIndex,
    onNext,
    onNextCompanyQuestions,
    onSkip,
    onBack,
    getStepResponse,
    hasAnyResponses,
    saveAndResumeSupported,
    saveProgressExpiresAt,
    autoSaveEnabled,
    onSaveAndExit,
    isSavingProgress,
    assessmentLoading,
    assessmentError,
    isLoadingResults,
    reportAssessmentEvent,
  } = useAssessment();

  const { t } = useTranslation(["assessment"]);
  // Only the backend knows whether this attempt runs on the architecture that
  // can persist progress, so offer save-and-exit on its verdict alone and fall
  // back to the plain exit confirmation otherwise.
  const saveAndResumeEnabled = saveAndResumeSupported;
  const { user } = useAuth();
  const orderedDomains = buildOrderedDomains(steps ?? []);
  const companySteps = (steps ?? []).filter(
    isCommercialQuestionStep
  ) as AssessmentQuestionStep[];

  const navigate = useNavigate();
  const location = useLocation();
  const { removePatternOverlay, onChangeColors } = usePatternBackground();

  const [isExitAssessmentModalOpen, setIsExitAssessmentModalOpen] =
    useState<boolean>(false);
  const [isCompanyQuestionsModalOpen, setIsCompanyQuestionsModalOpen] =
    useState<boolean>(false);
  const [isCompletingAssessment, setIsCompletingAssessment] =
    useState<boolean>(false);
  const [hasSaveProgressError, setHasSaveProgressError] =
    useState<boolean>(false);
  const [autoSavedExit, setAutoSavedExit] = useState<"saving" | "saved" | null>(
    null
  );
  // Identifies the exit in progress, so one the learner walked away from by
  // keeping going cannot later pop the confirmation or the error over them.
  const autoSavedExitAttempt = useRef(0);

  useEffect(() => {
    localStorage.removeItem("shouldTakeAssessment");
  }, []);

  useEffect(() => {
    // The step components paint a solid cream background — disable the
    // canvas pattern entirely so it never flashes through underneath
    // instead of leaving it running behind the content.
    onChangeColors(
      BluePattern.color.pattern.value,
      false,
      ASSESSMENT_BG_COLOR,
      false
    );
    removePatternOverlay(true);
    return () => {
      removePatternOverlay(false);
    };
  }, [removePatternOverlay, onChangeColors]);

  // The commercial-questions section intro duplicates the end screen's copy
  // — never let it render as its own step, forwards or back.
  useEffect(() => {
    if (!step) return;
    if (isCommercialSectionStep(step)) {
      onNext();
    }
  }, [step, onNext]);

  // Individual commercial questions are deferred entirely to the
  // company-questions modal off the end screen — advance past them without
  // recording an answer (the modal collects and submits these separately).
  useEffect(() => {
    if (!step) return;
    if (isCommercialQuestionStep(step)) {
      onSkip();
    }
  }, [step, onSkip]);

  // Read through a ref: this listener registers once on mount, and re-running
  // the effect to pick up a new closure would push a fresh history entry each
  // render. Assigned below, once the handler it points at exists.
  const requestExitRef = useRef<() => void>(() => undefined);

  // Intercept the browser back button so it surfaces the exit-confirmation
  // modal instead of silently navigating away mid-assessment.
  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href);
      requestExitRef.current();
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Commercial steps are auto-skipped forward and never painted, so
  // reporting them would claim the user saw a screen they never did.
  // Skipping the whole block in one tick also emitted several events
  // sharing a millisecond, which collide on the user_events uniqueness
  // constraint and fail the mutation.
  const lastLoggedStepIdRef = useRef<string | null>(null);

  // Shared by the in-flow path and the company-questions modal so the two
  // cannot drift — analytics consumers depend on both emitting the same
  // shape for the same step.
  const buildStepViewedParams = (viewedStep: AssessmentStep) => {
    const base = {
      user_id: userID,
      user_id_wone: userID,
      company_name: companyName,
      assessment_version: assessmentVersion,
      step_id: viewedStep.id,
      step_type: viewedStep.__typename,
      step_title: stepKey(viewedStep),
    };

    if (viewedStep.__typename === "AssessmentSectionStep") {
      return {
        ...base,
        section_id: viewedStep.id,
        section_name: stepKey(viewedStep),
      };
    }
    if (viewedStep.__typename === "AssessmentQuestionStep") {
      return {
        ...base,
        question_id: viewedStep.id,
        question_text: stepKey(viewedStep),
      };
    }
    return base;
  };

  const reportStepViewed = async (
    viewedStep: AssessmentStep,
    atMs?: number
  ) => {
    const params = buildStepViewedParams(viewedStep);

    await logAnalyticsEvent("assessment_step_viewed", params);

    reportAssessmentEvent({
      eventType: AssessmentEvent.AssessmentStepViewed,
      params,
      atMs,
    });
  };

  const logAssessmentStepViewed = async () => {
    if (!step) return;
    if (isCommercialSectionStep(step) || isCommercialQuestionStep(step)) {
      return;
    }
    if (lastLoggedStepIdRef.current === step.id) {
      return;
    }
    lastLoggedStepIdRef.current = step.id;

    await reportStepViewed(step);
  };

  useEffect(() => {
    logAssessmentStepViewed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step?.id]);

  // The modal renders every company question at once, so opening it is the
  // moment they all become visible — report one step-viewed each, matching
  // the shape in-flow steps emit. now + index gives each its own
  // millisecond; the ref keeps a reopen from double-reporting.
  const reportedCompanyQuestionsRef = useRef(false);

  const handleOpenCompanyQuestions = () => {
    setIsCompanyQuestionsModalOpen(true);

    if (reportedCompanyQuestionsRef.current) return;
    reportedCompanyQuestionsRef.current = true;

    const now = new Date().getTime();
    companySteps.forEach((companyStep, index) => {
      reportStepViewed(companyStep, now + index);
    });
  };

  const userID = localStorage.getItem("userID");
  const companyName = localStorage.getItem("companyName") || "NA";

  // The start itself is reported server-side by the startAssessment mutation;
  // this is the analytics stream that has no server-side equivalent.
  const logStartAssessment = async () => {
    await logAnalyticsEvent("start_assessment", {
      user_id: userID,
      user_id_wone: userID,
      company_name: companyName,
      assessment_version: assessmentVersion,
      step_id: step.id,
      step_type: step.__typename,
    });
  };

  const logAssessmentQuestionAnswered = async (
    params: {
      assessment_version: string;
      step_id: string;
      step_type: string;
      question_id: string;
      question_text: string;
      answer_value: string;
      answer_score?: number;
    },
    atMs?: number
  ) => {
    // GA limits: https://support.google.com/analytics/answer/9267744?hl=en
    let gaParams = {
      user_id: userID,
      user_id_wone: userID,
      company_name: companyName,
      ...params,
      question_text: params.question_text.substring(0, 100),
      answer_value: params.answer_value.substring(0, 100),
    };

    await logAnalyticsEvent("assessment_question_answered", gaParams);

    reportAssessmentEvent({
      eventType: AssessmentEvent.AssessmentQuestionAnswered,
      params,
      atMs,
    });
  };

  const logExitAssessment = async () => {
    const params = {
      assessment_version: assessmentVersion,
      step_id: step?.id,
      step_type: step?.__typename,
    };

    await logAnalyticsEvent("exit_assessment", {
      user_id: userID,
      user_id_wone: userID,
      company_name: companyName,
      ...params,
    });

    reportAssessmentEvent({
      eventType: AssessmentEvent.ExitAssessment,
      params,
    });
  };

  const handleStartAssessment = async () => {
    await logStartAssessment();
    onNext();
  };

  const handleAssessmentQuestionAnswered = async (
    option: SelectInputOption | TextInput,
    // Picker questions (duration/count) resolve the learner's raw pick to
    // its nearest authored bucket purely to derive a score/optionId — the
    // backend must still receive (and back-navigation must still restore)
    // exactly what the learner picked, e.g. "1 hr 15 mins", not the
    // bucket's own label ("20 mins - 1 hr 14 mins").
    rawTextOverride?: string
  ) => {
    if (step.__typename === "AssessmentQuestionStep") {
      if (step.input.__typename === "SelectInput") {
        const selected = option as SelectInputOption;
        const score = option?.value ? parseInt(option.value) : 0;
        // The option identifies itself; only a stepper's raw pick is text the
        // server can't derive. Sending the label back would send whichever
        // language the learner happened to be reading.
        const params = {
          assessment_version: assessmentVersion,
          step_id: step.id,
          step_type: step.__typename,
          input_type: step.input.__typename,
          question_id: step.id,
          question_text: stepKey(step),
          answer_value:
            rawTextOverride ?? answerKey(step, selected),
          answer_score: score,
        };

        await logAssessmentQuestionAnswered(params);
        onNext({
          text: rawTextOverride,
          score,
          optionId: selected.id,
        });
      } else if (step.input.__typename === "TextInput") {
        const params = {
          assessment_version: assessmentVersion,
          step_id: step.id,
          step_type: step.__typename,
          input_type: step.input.__typename,
          question_id: step.id,
          question_text: stepKey(step),
          answer_value: option.value,
        };

        await logAssessmentQuestionAnswered(params);
        onNext({
          text: option.value,
          score: 0,
        });
      }
    } else {
      throw new Error(
        `${step.__typename} is not of type AssessmentQuestionStep`
      );
    }
  };

  // Duration questions (vigorous/moderate activity, sleep) are answered via
  // an Hours/Minutes stepper rather than a tap-to-select card, but still
  // score against the step's own authored options — resolve the closest
  // option client-side purely for score/optionId, while the backend
  // records (and back-navigation restores) the raw picked duration.
  const handleAssessmentDurationAnswered = (hours: number, minutes: number) => {
    if (!isDurationQuestionStep(step)) {
      throw new Error(`${step.__typename} is not a duration question step`);
    }

    const totalMinutes = hours * 60 + minutes;
    const nearestOption = bucketFor(
      totalMinutes,
      (step.input as SelectInput).options
    );

    if (!nearestOption) {
      throw new Error(
        `No authored option matched a duration of ${totalMinutes} minutes for step ${step.id}`
      );
    }

    handleAssessmentQuestionAnswered(
      nearestOption,
      formatDuration(hours, minutes)
    );
  };

  // Count questions (alcohol/caffeine intake) are answered via a single -/+
  // stepper, but still score against the step's own authored options —
  // resolve the closest bucket client-side purely for score/optionId,
  // while the backend records (and back-navigation restores) the raw
  // picked count.
  const handleAssessmentCountAnswered = (count: number) => {
    if (!isCountQuestionStep(step)) {
      throw new Error(`${step.__typename} is not a count question step`);
    }

    const nearestOption = bucketFor(
      count,
      (step.input as SelectInput).options
    );

    if (!nearestOption) {
      throw new Error(
        `No authored option matched a count of ${count} for step ${step.id}`
      );
    }

    handleAssessmentQuestionAnswered(nearestOption, String(count));
  };

  // A ref can't disable the button — it doesn't re-render — so a second click
  // landing before the completion mutation resolved sent a duplicate event.
  const handleAssessmentCompleted = async () => {
    if (isCompletingAssessment) return;
    setIsCompletingAssessment(true);
    try {
      await onNext();
    } catch (error) {
      // The end step navigates away on success, so only a failure needs the
      // button back — otherwise the user is stranded on a dead control.
      setIsCompletingAssessment(false);
      throw error;
    }
  };

  const handleCompanyQuestionsSubmit = async (
    responses: CompanyQuestionResponse[]
  ) => {
    // now + index gives each event its own millisecond. Awaiting the loop is
    // not enough on its own — only the analytics call is awaited, the event
    // mutation is fire-and-forget — so without an explicit timestamp the batch
    // races and collides on the user_events uniqueness constraint
    // (user_id, type_id, platform, user_reported_at).
    const now = new Date().getTime();
    for (let index = 0; index < responses.length; index += 1) {
      const response = responses[index];
      const question = companySteps.find((step) => step.id === response.stepId);
      await logAssessmentQuestionAnswered(
        {
          assessment_version: assessmentVersion,
          step_id: response.stepId,
          step_type: "AssessmentQuestionStep",
          question_id: response.stepId,
          question_text: question ? stepKey(question) : "",
          // Free text where the learner typed one, otherwise the option they
          // picked — never its label, which is translated.
          answer_value: response.text ?? response.optionId ?? "",
          answer_score: response.score,
        },
        now + index
      );
    }

    onNextCompanyQuestions(responses);
  };

  const leaveAssessment = () => {
    const referrer = window.document.referrer;

    if (referrer) {
      const url = new URL(referrer);

      if (url.pathname === location.pathname) {
        navigate("/user/woneindex");
      } else {
        navigate(url.pathname, {
          state: { referrer: location.pathname },
          replace: true,
        });
      }
    } else {
      navigate("/user/home");
    }
  };

  // Only the discard path reports its own exit. Saving and exiting goes through
  // saveAndExitAssessment, which reports the exit server-side.
  const handleExitAssessment = async () => {
    await logExitAssessment();
    leaveAssessment();
  };

  // Nothing is answered yet on the welcome screen or a section intro ahead of
  // its first question, so there is nothing a save-progress modal could offer
  // to keep — the backend would hand back a fresh-start assessment either way.
  // Exit straight through rather than prompting over data that doesn't exist.
  // The plain exit-confirmation path is deliberately left alone.
  const requestExit = () => {
    if (saveAndResumeEnabled && !hasAnyResponses) {
      handleExitAssessment();
      return;
    }

    // Past the deadline a save still succeeds but the next start is fresh, so
    // only the save-and-exit prompt can describe what happens.
    if (
      saveAndResumeEnabled &&
      autoSaveEnabled &&
      !savedProgressExpired(saveProgressExpiresAt)
    ) {
      exitWithAutoSave();
      return;
    }

    setIsExitAssessmentModalOpen(true);
  };

  // Answers are already being saved as they are given, so there is nothing to
  // ask: confirm the save instead, and only once it is confirmed. The exit is
  // still recorded through the same save-and-exit call. If that cannot
  // confirm the answers are kept, fall back to the save-and-exit prompt with
  // its error, so the learner can retry or carry on.
  const exitWithAutoSave = async () => {
    if (autoSavedExit !== null) return;
    const attempt = ++autoSavedExitAttempt.current;
    setAutoSavedExit("saving");
    try {
      await onSaveAndExit();
    } catch (error) {
      logError("Error saving assessment progress", error);
      if (attempt !== autoSavedExitAttempt.current) return;
      setAutoSavedExit(null);
      setHasSaveProgressError(true);
      setIsExitAssessmentModalOpen(true);
      return;
    }
    if (attempt !== autoSavedExitAttempt.current) return;
    if (savedProgressExpired(saveProgressExpiresAt)) {
      setAutoSavedExit(null);
      setIsExitAssessmentModalOpen(true);
      return;
    }
    setAutoSavedExit("saved");
  };

  // Nothing in the save path times out, so a learner who only meant to close
  // must be able to get back to the assessment while it is still saving.
  const keepGoingAfterAutoSavedExit = () => {
    autoSavedExitAttempt.current += 1;
    setAutoSavedExit(null);
  };

  requestExitRef.current = requestExit;

  // A failed save must not navigate away — that would drop the answers while
  // telling the learner they were kept. Keep them in the modal with the error
  // instead, so retrying or carrying on are both still open to them.
  const handleSaveProgressAndExit = async () => {
    setHasSaveProgressError(false);
    try {
      await onSaveAndExit();
    } catch (error) {
      logError("Error saving assessment progress", error);
      setHasSaveProgressError(true);
      return;
    }
    leaveAssessment();
  };

  const closeExitModal = () => {
    setHasSaveProgressError(false);
    setIsExitAssessmentModalOpen(false);
  };

  const renderExitModal = () => {
    if (autoSavedExit !== null) {
      return (
        <SaveProgressModal
          variant="saved"
          onDone={leaveAssessment}
          onKeepGoing={keepGoingAfterAutoSavedExit}
          expiresAt={saveProgressExpiresAt}
          isSaving={autoSavedExit === "saving"}
        />
      );
    }

    if (!isExitAssessmentModalOpen) return null;

    if (saveAndResumeEnabled) {
      return (
        <SaveProgressModal
          onKeepGoing={closeExitModal}
          onSaveAndExit={handleSaveProgressAndExit}
          expiresAt={saveProgressExpiresAt}
          isSaving={isSavingProgress}
          hasSaveError={hasSaveProgressError}
        />
      );
    }

    return (
      <AssessmentModal
        subtitle={t("assessment:exitConfirm.title")}
        onYes={handleExitAssessment}
        onNo={closeExitModal}
      />
    );
  };

  if (assessmentError) {
    return (
      <PageError>
        <AssessmentModal
          title={t("assessment:actions.exit")}
          subtitle={assessmentError.message}
          onOkay={handleExitAssessment}
        />
      </PageError>
    );
  }

  if (assessmentLoading || !step) {
    // The welcome screen has its own loading state (header + spinner over
    // the gradient) so the generic white PageLoading never has to render
    // underneath before swapping to it — the first assessment load is
    // always the welcome step (currentstepIndex starts at 0).
    return (
      <>
        <AssessmentWelcomeUplift
          isLoading
          onBegin={handleStartAssessment}
          onClose={requestExit}
        />
        {renderExitModal()}
      </>
    );
  }

  const renderUpliftStep = (): React.ReactNode => {
    if (isCommercialSectionStep(step) || isCommercialQuestionStep(step)) {
      return (
        <div
          className={styles["assessment-loading"]}
          data-testid="assessment-loading"
        >
          <PageLoading />
        </div>
      );
    }

    if (step.__typename === "AssessmentWelcomeStep") {
      return (
        <AssessmentWelcomeUplift
          onBegin={handleStartAssessment}
          onClose={requestExit}
        />
      );
    }

    const progressPillFor = (
      number: number | null
    ): ProgressPillProps | null =>
      number
        ? {
            number,
            domain: orderedDomains[number - 1],
            totalDomains: orderedDomains.length,
          }
        : null;

    if (
      step.__typename === "AssessmentSectionStep" ||
      step.__typename === "AssessmentInfoStep"
    ) {
      return (
        <AssessmentSectionUplift
          stepKey={step.id}
          titleHtml={step.titleHtml}
          subtitleHtml={step.subtitleHtml}
          onContinue={() => onNext()}
          onBack={currentstepIndex > 0 ? onBack : undefined}
          onClose={requestExit}
          progressPill={progressPillFor(
            getSectionProgressNumber(currentstepIndex, steps, orderedDomains)
          )}
        />
      );
    }

    if (isDurationQuestionStep(step)) {
      const previousResponse = getStepResponse(step.id);
      const previousDuration = previousResponse?.text
        ? parseDurationResponse(previousResponse.text)
        : null;

      return (
        <AssessmentQuestionDurationUplift
          key={step.id}
          stepKey={step.id}
          titleHtml={step.titleHtml}
          subtitleHtml={step.subtitleHtml}
          defaultHours={
            previousDuration?.hours ??
            (step.shorthand === SLEEP_DURATION_SHORTHAND ? 7 : 0)
          }
          defaultMinutes={previousDuration?.minutes ?? 0}
          onSubmit={handleAssessmentDurationAnswered}
          onBack={currentstepIndex > 0 ? onBack : undefined}
          onClose={requestExit}
          progressPill={progressPillFor(
            getQuestionProgressNumber(step, orderedDomains)
          )}
        />
      );
    }

    if (isCountQuestionStep(step)) {
      const previousResponse = getStepResponse(step.id);
      const previousCount = previousResponse?.text
        ? Number(previousResponse.text)
        : null;

      return (
        <AssessmentQuestionCountUplift
          key={step.id}
          stepKey={step.id}
          titleHtml={step.titleHtml}
          subtitleHtml={step.subtitleHtml}
          unitLabel={t(
            PER_DAY_COUNT_SHORTHANDS.has(step.shorthand ?? "")
              ? "assessment:question.perDayUnit"
              : "assessment:question.drinksUnit"
          )}
          hasEquivalentsTable={EQUIVALENTS_SHORTHANDS.has(step.shorthand ?? "")}
          defaultCount={
            previousCount != null && !Number.isNaN(previousCount)
              ? previousCount
              : 0
          }
          onSubmit={handleAssessmentCountAnswered}
          onBack={currentstepIndex > 0 ? onBack : undefined}
          onClose={requestExit}
          progressPill={progressPillFor(
            getQuestionProgressNumber(step, orderedDomains)
          )}
        />
      );
    }

    if (isAssessmentQuestionStep(step) && isSelectInput(step.input)) {
      return (
        <AssessmentQuestionUplift
          stepKey={step.id}
          titleHtml={step.titleHtml}
          subtitleHtml={step.subtitleHtml}
          options={step.input.options}
          selectedOptionId={getStepResponse(step.id)?.optionId ?? null}
          onSelectOption={handleAssessmentQuestionAnswered}
          onBack={currentstepIndex > 0 ? onBack : undefined}
          onClose={requestExit}
          progressPill={progressPillFor(
            getQuestionProgressNumber(step, orderedDomains)
          )}
        />
      );
    }

    if (
      isAssessmentQuestionStep(step) &&
      isTextInput(step.input) &&
      step.input.type === "number"
    ) {
      return (
        <AssessmentQuestionNumberUplift
          key={step.id}
          stepKey={step.id}
          titleHtml={step.titleHtml}
          min={step.input.min}
          max={step.input.max}
          defaultValue={
            getStepResponse(step.id)?.text
              ? Number(getStepResponse(step.id)?.text)
              : undefined
          }
          onSubmit={(value) =>
            handleAssessmentQuestionAnswered({
              ...(step.input as TextInput),
              value,
            })
          }
          onBack={currentstepIndex > 0 ? onBack : undefined}
          onClose={requestExit}
          progressPill={progressPillFor(
            getQuestionProgressNumber(step, orderedDomains)
          )}
        />
      );
    }

    if (step.__typename === "AssessmentEndStep") {
      if (companySteps.length === 0) {
        return <AutoCompleteEndStep onComplete={handleAssessmentCompleted} />;
      }

      return (
        <AssessmentEndUplift
          firstName={user?.firstName}
          hasCompanyQuestions={companySteps.length > 0}
          onContinue={handleOpenCompanyQuestions}
          onSubmit={handleAssessmentCompleted}
          isSubmitting={isLoadingResults || isCompletingAssessment}
          onBack={currentstepIndex > 0 ? onBack : undefined}
        />
      );
    }

    return <div data-testid="assessment-page-uplift" />;
  };

  return (
    <>
      {renderUpliftStep()}
      {renderExitModal()}
      {isCompanyQuestionsModalOpen && (
        <CompanyQuestionsModal
          questions={companySteps}
          onSubmit={handleCompanyQuestionsSubmit}
          onClose={() => setIsCompanyQuestionsModalOpen(false)}
          isSubmitting={isLoadingResults}
        />
      )}
    </>
  );
};

export default Assessment;
